<template>
  <div class="relative shrink-0 w-9 h-9" ref="bellWrapper">
    <span
      v-if="store.hasUnread"
      class="bell-button__ping pointer-events-none"
      aria-hidden="true"
    >
      <span class="bell-button__ping-wave"></span>
      <span class="bell-button__ping-wave bell-button__ping-wave--delay"></span>
    </span>
    <button
      type="button"
      class="relative z-[1] inline-flex items-center justify-center rounded-full w-full h-full"
      :class="
        store.hasUnread
          ? 'bg-primary/12 text-primary ring-2 ring-primary/25 hover:bg-primary/18 shadow-sm shadow-primary/15'
          : 'bg-secondary/10 text-secondary hover:bg-secondary/20 transition-all duration-200'
      "
      :aria-label="t('notifications_title')"
      :aria-expanded="isOpen"
      @click.stop="onBellClick"
      @mouseenter="onBellHover"
      @focus="onBellHover"
    >
      <i
        class="relative z-[1] mdi text-[18px] origin-top"
        :class="[
          store.hasUnread ? 'mdi-bell-ring' : 'mdi-bell-outline',
          { 'bell-wiggle': bellWiggle },
        ]"
      ></i>
      <span
        v-if="store.hasUnread"
        class="notification-badge absolute z-[2] flex items-center justify-center min-w-[1.125rem] h-[1.125rem] px-1 text-[9px] font-bold leading-none text-white rounded-full bg-rose-500 ring-2 ring-white dark:ring-slate-900 -top-0.5"
        :class="lang === 'ar' ? '-left-0.5' : '-right-0.5'"
        aria-hidden="true"
      >
        {{ store.badgeLabel }}
      </span>
    </button>

    <transition name="panel">
      <NotificationDropdown
        v-if="isOpen"
        :items="store.dropdownItems"
        :initial-loading="store.dropdownInitialLoading"
        :refreshing="store.dropdownRefreshing"
        :loaded="store.dropdownLoaded"
        :error="store.dropdownError"
        :unread-count="store.unreadCount"
        :lang="lang"
        :t="t"
        :panel-styles="panelStyles"
        @close="closeDropdown"
        @retry="refreshDropdown"
      />
    </transition>
  </div>
</template>

<script setup>
import { ref, watch, onMounted, onBeforeUnmount } from "vue";
import { useRouter } from "vue-router";
import NotificationDropdown from "./NotificationDropdown.vue";
import { useNotificationStore } from "@/stores/notifications";

const props = defineProps({
  lang: { type: String, default: "ar" },
  t: { type: Function, required: true },
});

const router = useRouter();
const store = useNotificationStore();

const isOpen = ref(false);
const bellWrapper = ref(null);
const panelStyles = ref({});
const bellWiggle = ref(false);

let skipUnreadWatch = true;
let wiggleTimer = null;

function prefersReducedMotion() {
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

function playBellWiggle() {
  if (prefersReducedMotion()) return;

  bellWiggle.value = false;
  requestAnimationFrame(() => {
    bellWiggle.value = true;
  });

  clearTimeout(wiggleTimer);
  wiggleTimer = setTimeout(() => {
    bellWiggle.value = false;
  }, 650);
}

watch(
  () => store.unreadCount,
  (next, prev) => {
    if (skipUnreadWatch) {
      skipUnreadWatch = false;
      return;
    }
    if (next > (prev ?? 0)) {
      playBellWiggle();
    }
  },
);

function isMobileView() {
  return window.innerWidth < 640;
}

function positionPanel() {
  if (!bellWrapper.value) return;

  const rect = bellWrapper.value.getBoundingClientRect();
  const panelWidth = Math.min(window.innerWidth - 24, 384);
  const gap = 10;
  const edge = 12;
  const isRtl = props.lang === "ar";

  if (isRtl) {
    let left = rect.right - panelWidth;
    const maxLeft = window.innerWidth - panelWidth - edge;
    if (left > maxLeft) left = maxLeft;
    if (left < edge) left = edge;

    panelStyles.value = {
      top: `${rect.bottom + gap}px`,
      left: `${left}px`,
      right: "auto",
    };
  } else {
    let right = window.innerWidth - rect.right;
    const maxRight = window.innerWidth - panelWidth - edge;
    if (right > maxRight) right = maxRight;
    if (right < edge) right = edge;

    panelStyles.value = {
      top: `${rect.bottom + gap}px`,
      right: `${right}px`,
      left: "auto",
    };
  }
}

function openDropdown() {
  isOpen.value = true;
  positionPanel();
  store.refreshDropdownOnOpen();
}

function closeDropdown() {
  isOpen.value = false;
}

function onBellHover() {
  if (isMobileView() || isOpen.value) return;
  store.schedulePrefetchDropdown();
}

function refreshDropdown() {
  store.fetchDropdownList({ force: true, background: store.hasDropdownCache });
}

function onBellClick() {
  if (isMobileView()) {
    router.push({ name: "notifications.index" });
    return;
  }

  if (isOpen.value) {
    closeDropdown();
  } else {
    openDropdown();
  }
}

function handleClickOutside(event) {
  const wrapper = bellWrapper.value;
  if (wrapper && !wrapper.contains(event.target)) {
    closeDropdown();
  }
}

function handleResize() {
  if (isOpen.value) positionPanel();
}

onMounted(() => {
  document.addEventListener("click", handleClickOutside);
  window.addEventListener("resize", handleResize);
});

onBeforeUnmount(() => {
  document.removeEventListener("click", handleClickOutside);
  window.removeEventListener("resize", handleResize);
  store.cancelPrefetch();
  clearTimeout(wiggleTimer);
});
</script>

<style scoped>
.panel-enter-active,
.panel-leave-active {
  transition:
    opacity 0.18s ease,
    transform 0.18s ease;
}
.panel-enter-from,
.panel-leave-to {
  opacity: 0;
  transform: translateY(-6px) scale(0.98);
}

.bell-button__ping {
  position: absolute;
  inset: 0;
  z-index: 0;
}

.bell-button__ping-wave {
  position: absolute;
  inset: 0;
  border-radius: 9999px;
  animation: bell-edge-ping 2s cubic-bezier(0, 0, 0.2, 1) infinite;
}

.bell-button__ping-wave--delay {
  animation-delay: 1s;
}

/* Box-shadow spread grows outward from the circle edge only — never inward. */
@keyframes bell-edge-ping {
  0% {
    box-shadow: 0 0 0 2px rgb(5 139 128 / 0.2);
  }
  100% {
    box-shadow: 0 0 0 12px rgb(5 139 128 / 0);
  }
}

.bell-wiggle {
  animation: bell-wiggle 0.62s ease-in-out;
}

@keyframes bell-wiggle {
  0%,
  100% {
    transform: rotate(0deg);
  }
  18% {
    transform: rotate(14deg);
  }
  36% {
    transform: rotate(-12deg);
  }
  54% {
    transform: rotate(9deg);
  }
  72% {
    transform: rotate(-5deg);
  }
}

@media (prefers-reduced-motion: reduce) {
  .bell-button__ping-wave,
  .bell-wiggle {
    animation: none !important;
  }
}
</style>
